import _ from 'lodash';
import React from 'react';
import Debug from 'debug';
import { NavLink } from 'fluxible-router';
import Image from '../Image';
import shallowCompare from '../../lib/shallowCompare';
import getThumbnailPhotoUrlFromHash from '../../lib/getThumbnailPhotoUrlFromHash';
import Customer from '../../models/Customer';
import ShoppingCart from '../../models/ShoppingCart';
import Store from '../../models/Store';
import StoreFeed from '../../models/StoreFeed';
import CollectionTile from '../CollectionTile';
import InventoryRecordCard from '../InventoryRecordCard';
import styles from './index.sass';
import ChevronPng from './images/list_chevron.png';
import ChevronSvg from './images/list_chevron.svg';
import TealChevronPng from '../images/chevron_teal.png';
import TealChevronSvg from '../images/chevron_teal.svg';

const debug = Debug('delectable');

class StoreFeedLayout extends React.Component {
  static contextTypes = {
    executeAction : React.PropTypes.func,
    getUserAgent  : React.PropTypes.func
  };

  static propTypes = {
    shoppingCart           : React.PropTypes.instanceOf(ShoppingCart),
    store                  : React.PropTypes.instanceOf(Store),
    storeFeed              : React.PropTypes.instanceOf(StoreFeed),
    customer               : React.PropTypes.instanceOf(Customer)
  };

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  renderFeed(feed) {
    if(!feed) {return null;}
    return feed.get('items').map((item, index) => {
      let props = {
        key: index,
        store: this.props.store,
        shoppingCart: this.props.shoppingCart,
        customer: this.props.customer,
        ...item.get('object')
      };
      switch(item.get('type')) {
        case 'text':
          return <TextFeedItem {...props} />;
        case 'photo':
          return <PhotoFeedItem {...props} />;
        case 'carousel':
          return <CarouselFeedItem {...props} />;
        case 'link_list':
          return <LinkListFeedItem {...props} />;
        case 'product_group':
          return <ProductGroupFeedItem {...props} />;
      }
    });
  }

  render() {
    let { store, storeFeed } = this.props;
    if(!store || !storeFeed) {
      return (
        <div className={styles.store} itemScope itemType="http://schema.org/Organization">
          <span className="loading-spinner--teal" style={{display: 'block', marginTop: 60, marginBottom: 160}} />
        </div>
      );
    }

    let imageProps = {
      className: styles.header,
      tag: 'div',
      aspectRatio: (1440 / 460),
      mobileAspectRatio: (375 / 230),
      targetWidth: 1280,
      photoHash: store.background_photo_hash,
      borderRadius: '0'
    };

    return (
      <div className={styles.store} itemScope itemType="http://schema.org/Organization">
        <Image {...imageProps}>
          <div className={styles.heroOverlay}>
            <h2 className={styles.marketingMessage}>{store.marketing_message}</h2>
            <div className={styles.linkWrapper}>
              <NavLink className={styles.shopLink} routeName="search">Shop now</NavLink>
            </div>
          </div>
        </Image>
        <div className={styles.feed}>
          {this.renderFeed(storeFeed)}
        </div>
      </div>
    );
  }
}

export default StoreFeedLayout;

function TextFeedItem(props) {
  let header = props.header ? <h3 className={styles.feedItemHeader}>{props.header}</h3> : null;
  return (
    <div className={styles.textFeedItem}>
      {header}
      <p className={styles.body}>{props.text}</p>
    </div>
  );
}

function PhotoFeedItem(props) {
  let header = props.header ? <h3 className={styles.feedItemHeader}>{props.header}</h3> : null;
  let contentProps = {
    className: styles.content,
    style: {
      background: `url('${getThumbnailPhotoUrlFromHash(props.photo_hash, 940)}') no-repeat center center`,
      backgroundSize: 'cover'
    }
  };
  return (
    <a className={styles.photoFeedItem} href={props.deeplink}>
      {header}
      <div className={styles.photo}>
        <div {...contentProps}/>
        <p className={styles.text}>{props.text}</p>
      </div>
    </a>
  );
}

function CarouselFeedItem(props) {
  let header = props.header ? <h3 className={styles.feedItemHeader}>{props.header}</h3> : null;
  return (
    <div className={styles.carouselFeedItem}>
      {header}
      <div className={styles.listWrapper}>
        <div className={styles.listScrollContainer}>
          <div className={styles.list}>
            {props.items.map((item, index) => {
              return <CollectionTile collection={item.object.collection} key={index} />;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkListFeedItem(props) {
  let header = props.header ? <h3 className={styles.feedItemHeader}>{props.header}</h3> : null;
  return (
    <div className={styles.linkListFeedItem}>
      {header}
      <ul className={styles.list}>
        {props.items.map((item, index) => {
          return (
            <li className={styles.listItem} key={index}>
              <a className={styles.listItemLink} href={item.deeplink}>
                <span className={styles.text}>{item.text}</span>
                <img className={styles.chevron} src={ChevronPng} srcSet={ChevronSvg} />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProductGroupFeedItem(props) {
  let { store, shoppingCart, customer } = props;
  let seeAllLink = props.url ? <NavLink className={styles.seeAllLink} href={props.url}>See all <img className={styles.chevron} src={TealChevronPng} srcSet={TealChevronSvg} /></NavLink> : null;
  let header = props.header ? <h3 className={styles.productGroupFeedItemHeader}>{props.header}{seeAllLink}</h3> : null;
  let description = props.description ? <p className={styles.productGroupFeedItemDescription}>{props.description}</p> : null;
  let subheader = props.subheader ? <p className={styles.productGroupFeedItemSubheader}>{props.subheader}</p> : null;
  return (
    <div className={styles.productGroupFeedItem}>
      {header}
      {subheader}
      {description}
      <div className={styles.listWrapper}>
        <div className={styles.listScrollContainer}>
          <div className={styles.list}>
            {props.inventory_records.map((inventoryRecord, key) => {
              let item = shoppingCart && shoppingCart.findItemByInventoryRecordId(inventoryRecord.id);
              let cardProps = {
                key,
                inventoryRecord,
                item,
                store,
                customer
              };
              return <InventoryRecordCard {...cardProps} />;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}





// WEBPACK FOOTER //
// banquet/components/StoreFeedLayout/index.js